import React from 'react';

interface SliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  showValue?: boolean;
  formatValue?: (value: number) => string;
  step?: number;
}

export const Slider: React.FC<SliderProps> = ({
  min,
  max,
  value,
  onChange,
  label,
  showValue = true,
  formatValue = (v) => v.toString(),
  step = 1,
}) => {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="w-full">
      {(label || showValue) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm text-muted">{label}</span>}
          {showValue && (
            <span className="text-sm font-mono text-fg">{formatValue(value)}</span>
          )}
        </div>
      )}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        step={step}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        className="slider-fill w-full h-1 rounded-full block"
        style={{
          background: `linear-gradient(to right, rgb(var(--cs-accent)) 0%, rgb(var(--cs-accent)) ${percentage}%, rgb(var(--cs-line-strong)) ${percentage}%, rgb(var(--cs-line-strong)) 100%)`,
        }}
      />
    </div>
  );
};

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  label?: string;
  formatValue?: (value: number) => string;
  step?: number;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  label,
  formatValue = (v) => v.toString(),
  step = 1,
}) => {
  const handleMinChange = (newMin: number) => {
    if (newMin <= value[1]) {
      onChange([newMin, value[1]]);
    }
  };

  const handleMaxChange = (newMax: number) => {
    if (newMax >= value[0]) {
      onChange([value[0], newMax]);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted">{label}</span>
          <span className="text-sm font-mono text-fg">
            {formatValue(value[0])} – {formatValue(value[1])}
          </span>
        </div>
      )}
      <div className="relative pt-1">
        <div className="relative h-1 bg-line-strong rounded-full">
          <div
            className="absolute h-full bg-accent rounded-full"
            style={{
              left: `${((value[0] - min) / (max - min)) * 100}%`,
              right: `${100 - ((value[1] - min) / (max - min)) * 100}%`,
            }}
          />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          value={value[0]}
          step={step}
          aria-label={label ? `${label} minimum` : 'Minimum'}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="absolute top-0 w-full h-1 appearance-none bg-transparent pointer-events-none slider-thumb-only"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value[1]}
          step={step}
          aria-label={label ? `${label} maximum` : 'Maximum'}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="absolute top-0 w-full h-1 appearance-none bg-transparent pointer-events-none slider-thumb-only"
        />
      </div>
    </div>
  );
};
